import { useEffect, useState } from 'react';
import { ErreurApi } from '../api/client';
import { useEnregistrerProduit, useMajArticles, useRechercheProduits } from '../api/hooks';
import type { CommandeListee, ProduitReferentiel } from '../api/types';
import { useToasts } from './Toasts';
import { Repousser } from './Repousser';

/** Une ligne en cours d'édition. `nom` et `fournisseur` ne servent qu'à l'affichage. */
interface Ligne {
  reference: string;
  quantite: number | null;
  nom: string | null;
  fournisseur: string | null;
}

/**
 * Correction des lignes produit d'une commande.
 *
 * Elle existe pour un cas précis : une référence que l'import n'a pas su lire,
 * ou qui ne figure pas au référentiel. Sans ce chemin, une commande dont aucune
 * ligne n'est reconnue ne peut être transmise à personne, et rien dans
 * l'application ne permet de la réparer — il fallait passer par la base.
 *
 * Les produits s'ajoutent en cherchant dans le référentiel, jamais en tapant une
 * référence libre : c'est lui qui porte le fournisseur et la désignation, et une
 * référence inventée reproduirait exactement le problème qu'on répare.
 */
export function ArticlesCommande({
  commande, onFermer,
}: {
  commande: CommandeListee;
  onFermer: () => void;
}) {
  const { ajouter } = useToasts();
  const majour = useMajArticles();

  const [lignes, setLignes] = useState<Ligne[]>(() =>
    commande.articles.map((a) => ({
      reference: a.reference ?? '',
      quantite: a.quantite,
      nom: a.nom,
      fournisseur: a.fournisseur,
    })).filter((l) => l.reference));

  const [recherche, setRecherche] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const resultats = useRechercheProduits(recherche);

  // Création d'une référence absente du référentiel, sans quitter la fenêtre :
  // c'est le cas qui amène ici, et renvoyer l'utilisateur dans Réglages pour
  // revenir ensuite serait lui faire perdre le fil.
  const creer = useEnregistrerProduit();
  const [nouveau, setNouveau] = useState<{
    reference: string; nom: string; correspondance: string; unite: string; fournisseur: string;
  } | null>(null);

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  function ajouterProduit(p: ProduitReferentiel) {
    setErreur(null);
    // Une référence déjà présente n'est pas dupliquée : on incrémente, ce qui
    // est ce qu'on voulait dire en la choisissant une seconde fois.
    setLignes((precedentes) => {
      const i = precedentes.findIndex((l) => l.reference === p.reference);
      if (i === -1) {
        return [...precedentes, {
          reference: p.reference, quantite: 1, nom: p.nom, fournisseur: p.fournisseur,
        }];
      }
      return precedentes.map((l, j) =>
        j === i ? { ...l, quantite: (l.quantite ?? 0) + 1 } : l);
    });
  }

  async function enregistrer() {
    setErreur(null);
    try {
      await majour.mutateAsync({
        id: commande.id,
        corps: {
          articles: lignes.map((l) => ({ reference: l.reference, quantite: l.quantite })),
        },
      });
      ajouter(
        lignes.length
          ? `${lignes.length} ligne${lignes.length > 1 ? 's' : ''} enregistrée${lignes.length > 1 ? 's' : ''} pour ${commande.ref}.`
          : `Toutes les lignes de ${commande.ref} ont été retirées.`,
      );
      onFermer();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "L'enregistrement a échoué.");
    }
  }

  const dejaPresentes = new Set(lignes.map((l) => l.reference));

  async function creerReference() {
    if (!nouveau) return;
    setErreur(null);
    const reference = nouveau.reference.trim();
    if (!reference) { setErreur('La référence est obligatoire.'); return; }
    try {
      const p = await creer.mutateAsync({
        reference,
        nom: nouveau.nom.trim(),
        correspondance: nouveau.correspondance.trim() || null,
        unite: nouveau.unite || null,
        fournisseur: nouveau.fournisseur.trim() || null,
      });
      ajouter(`${reference} ajoutée au référentiel.`);
      ajouterProduit({
        reference: p.reference ?? reference,
        nom: p.nom ?? nouveau.nom.trim(),
        correspondance: p.correspondance ?? null,
        unite: p.unite ?? null,
        fournisseur: p.fournisseur ?? null,
        actif: true,
      });
      setNouveau(null);
      setRecherche('');
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "La référence n'a pas pu être créée.");
    }
  }

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="box" role="dialog" aria-modal="true" aria-label={`Articles de ${commande.ref}`}>
        <div className="mhead">
          <h2>Articles — {commande.ref}</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          <div className="field">
            <label htmlFor="ac-q">Ajouter un produit</label>
            <input
              id="ac-q" type="search" value={recherche}
              placeholder="référence ou désignation — la virgule sépare les termes"
              onChange={(e) => setRecherche(e.target.value)}
            />
          </div>

          {recherche.split(',').some((t) => t.trim().length >= 2) && (
            <div className="fieldset" style={{ maxHeight: 220, overflowY: 'auto' }}>
              {resultats.isLoading && <p className="indice">Recherche…</p>}
              {resultats.data && resultats.data.elements.length === 0 && !nouveau && (
                <>
                  <p className="indice">Aucune référence ne correspond à cette recherche.</p>
                  <button
                    type="button" className="btn sm"
                    onClick={() => setNouveau({
                      reference: recherche.split(',')[0]?.trim() ?? '',
                      nom: '', correspondance: '', unite: 'unité', fournisseur: '',
                    })}
                  >
                    Créer cette référence au référentiel
                  </button>
                </>
              )}
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {(resultats.data?.elements ?? []).map((p) => (
                  <li
                    key={p.reference}
                    style={{
                      display: 'flex', gap: 10, alignItems: 'baseline',
                      justifyContent: 'space-between', padding: '6px 0',
                      borderBottom: '1px solid var(--line)',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{p.nom || p.reference}</div>
                      <div className="sub">
                        <span className="mono">{p.reference}</span>
                        {' · '}
                        {p.fournisseur ?? (
                          <span style={{ color: 'var(--brick)' }}>fournisseur indéterminé</span>
                        )}
                        {!p.correspondance && (
                          <span style={{ color: 'var(--ocre)' }}> · sans correspondance</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button" className="btn sm" style={{ flex: '0 0 auto' }}
                      onClick={() => ajouterProduit(p)}
                    >
                      {dejaPresentes.has(p.reference) ? '+1' : 'Ajouter'}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {nouveau && (
            <div
              className="fieldset"
              style={{ borderLeft: '4px solid var(--jaune)', background: 'var(--ocre-soft)' }}
            >
              <h3 style={{ color: 'var(--ocre)' }}>Nouvelle référence au référentiel</h3>
              <p style={{ fontSize: 12.5, color: 'var(--ocre)', marginBottom: 10 }}>
                Elle servira à toutes les commandes suivantes. La <strong>correspondance</strong> est
                la désignation du fournisseur : c&apos;est elle qui part dans le mail.
              </p>

              <div className="field">
                <label htmlFor="np-ref">Référence</label>
                <input
                  id="np-ref" type="text" value={nouveau.reference}
                  onChange={(e) => setNouveau({ ...nouveau, reference: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="np-nom">Désignation interne</label>
                <input
                  id="np-nom" type="text" value={nouveau.nom}
                  placeholder="ce que nous affichons"
                  onChange={(e) => setNouveau({ ...nouveau, nom: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="np-cor">Correspondance fournisseur</label>
                <input
                  id="np-cor" type="text" value={nouveau.correspondance}
                  placeholder="ce que le fournisseur affiche"
                  onChange={(e) => setNouveau({ ...nouveau, correspondance: e.target.value })}
                />
              </div>
              <div className="rule">
                <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                  <label htmlFor="np-frs">Fournisseur</label>
                  <input
                    id="np-frs" type="text" value={nouveau.fournisseur}
                    placeholder="FORTEMIX"
                    onChange={(e) => setNouveau({ ...nouveau, fournisseur: e.target.value.toUpperCase() })}
                  />
                </div>
                <div className="field" style={{ flex: '0 0 130px', marginBottom: 0 }}>
                  <label htmlFor="np-uni">Unité</label>
                  <select
                    id="np-uni" value={nouveau.unite}
                    onChange={(e) => setNouveau({ ...nouveau, unite: e.target.value })}
                  >
                    <option value="unité">pièce(s)</option>
                    <option value="m2">m²</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <button
                  type="button" className="btn pri sm" disabled={creer.isPending}
                  onClick={() => void creerReference()}
                >
                  {creer.isPending ? <><span className="spin" /> Création…</> : 'Créer et ajouter'}
                </button>
                <button type="button" className="btn sm" onClick={() => setNouveau(null)}>
                  Annuler
                </button>
              </div>
            </div>
          )}

          <h3 style={{ margin: '14px 0 8px' }}>
            Lignes de la commande {lignes.length ? `(${lignes.length})` : ''}
          </h3>

          {lignes.length === 0 ? (
            <p className="err">
              Aucune ligne. La commande ne peut être adressée à aucun fournisseur — ajoutez au moins
              un produit, ou rédigez le mail à la main.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {lignes.map((l, i) => (
                <li
                  key={l.reference}
                  className="rule"
                  style={{ borderBottom: '1px solid var(--line)', paddingBottom: 8 }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{l.nom || l.reference}</div>
                    <div className="sub">
                      <span className="mono">{l.reference}</span>
                      {' · '}
                      {l.fournisseur ?? (
                        <span style={{ color: 'var(--brick)' }}>fournisseur indéterminé</span>
                      )}
                    </div>
                  </div>
                  <input
                    type="number" min={0} step={1} value={l.quantite ?? ''}
                    aria-label={`Quantité de ${l.reference}`}
                    style={{ width: 72 }}
                    onChange={(e) => {
                      const v = e.target.value === '' ? null : Math.max(0, Number(e.target.value));
                      setLignes((p) => p.map((x, j) => (j === i ? { ...x, quantite: v } : x)));
                    }}
                  />
                  <button
                    type="button" className="btn sm"
                    onClick={() => setLignes((p) => p.filter((_, j) => j !== i))}
                  >
                    Retirer
                  </button>
                </li>
              ))}
            </ul>
          )}

          {erreur && <p className="err" role="alert">{erreur}</p>}

          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 10 }}>
            Le fournisseur de chaque ligne est redéduit du référentiel à l&apos;enregistrement. Les
            lignes ne sont plus modifiables une fois la commande transmise.
          </p>
        </div>

        <div className="mfoot">
          <Repousser commande={commande} />
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          <button
            type="button" className="btn pri" disabled={majour.isPending}
            onClick={() => void enregistrer()}
          >
            {majour.isPending ? <><span className="spin" /> Enregistrement…</> : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
